import React, { useCallback, useEffect, useReducer, useState } from "react";
import { Stack } from "@mui/system";
import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Tooltip, Box } from "@mui/material";
import MaterialReactTable, { MRT_ColumnDef } from "material-react-table";
import { Link } from 'react-router-dom';
import { EsgFileIcon } from 'assets';
import { getAuditLogsData } from "api";

import {
  Avatar,
  EMPTY_CELL_VALUE,
  EsgPrimaryLabel,
  COLORS,
  Loading,
  MarginWrapper,
  usePagination,
  useTableExpandedRows,
  useYearMonthPeriodFilter,
  useYearPeriodFilter,
  EsgDatePickerField,
} from "components";
import moment from "moment";

import { useNotify } from "hooks";

import { LOCAL_STORAGE_KEYS } from "consts";

import { ParsedQueryParams } from "types";

import { useNewBreadcrumb } from "recoils";

import { getDateFormatset, getInitials, isNullOrUndefined } from "utils";
import { EsgCommonButton } from "components/inputs/esg-buttons";

import { AUDIT_LOGS_PAGE } from "./consts";
import { AuditLogsDetailsModal } from "./components";

const initialState = {
  isYearly: false,
  startDate: moment(new Date()).format("YYYY-MM-DD"),
  endDate: moment(new Date()).format("YYYY-MM-DD"),
  fromDate: null,
  toDate: null,
  view: "monthly",
};

function reducer(state: any, action: any) {
  let isYearly;
  let period;

  switch (action.type) {
    case "SET_YEARLY":
      ({ isYearly, period } = action.payload);
      return {
        ...state,
        isYearly,
        view: isYearly ? "yearly" : "monthly",
        startDate: isYearly
          ? period?.startOf("year").format("YYYY-MM-DD")
          : `${period.format("YYYY-MM")}-01`,
        endDate: isYearly
          ? period?.endOf("year").format("YYYY-MM-DD")
          : `${period.format("YYYY-MM")}-${period?.daysInMonth()}`,
      };
    case "SET_FROM_DATE":
      return {
        ...state,
        fromDate: action.payload.value,
        view: "daterange",
        startDate: action.payload.value
      };
    case "SET_TO_DATE":
      return {
        ...state,
        toDate: action.payload.value,
        view: "daterange",
        endDate: action.payload.value
      };
    case "SET_START_AND_END_DATES":
      return {
        ...state,
        startDate: action.payload.startDate,
        endDate: action.payload.endDate,
      };

    case "CLEAR_DATES":
      return {
        ...state,
        fromDate: null,
        toDate: null,
        startDate: moment(new Date()).format("YYYY-MM-DD"),
        endDate: moment(new Date()).format("YYYY-MM-DD"),
        isYearly: false,
        view: "monthly",
      };
    default:
      return state;
  }
}

export const AuditLogsPage = () => {
  // ---- States ----
  const [state, dispatch] = useReducer(reducer, initialState);
  const [clearDate, setClearDate] = useState(false);
  const { isYearly, startDate, endDate, fromDate } = state;
  const [dateError, setDateError] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [isChanged, setChanged] = useState(false)
  const [selectedRowData, setSelectedRowData] = useState<any>(undefined);
  // ---- Hook ----
  const notify = useNotify();

  useNewBreadcrumb(AUDIT_LOGS_PAGE.TITLE);
  const { setInitialExpanded, updateQueryParamsWithInitialExpanded } =
    useTableExpandedRows();

  const { setInitialPage, updateQueryParamsWithInitialPage } = usePagination({
    pageSizeLocalStorageKey: LOCAL_STORAGE_KEYS.EMISSION_SUMMARY_SIZE,
    updateExternalStates: setInitialExpanded,
    getQueryParamsWithExternalChanges: updateQueryParamsWithInitialExpanded,
  });
  const updateExternalStates = useCallback(() => {
    setInitialPage();
    setInitialExpanded();
  }, [setInitialPage, setInitialExpanded]);

  const getQueryParamsWithExternalChanges = useCallback(
    (queryParams: ParsedQueryParams) =>
      updateQueryParamsWithInitialExpanded(
        updateQueryParamsWithInitialPage(queryParams),
      ),
    [updateQueryParamsWithInitialExpanded, updateQueryParamsWithInitialPage],
  );
  const { period, availableYears } = useYearMonthPeriodFilter({
    updateExternalStates,
    getQueryParamsWithExternalChanges,
    isYearly,
  });

  const { year } = useYearPeriodFilter({
    updateExternalStates: undefined,
    withQueryParams: undefined,
    useYears: undefined,
    initialValue: dayjs().utc().year(),
  });

  const minDate = dayjs(
    `${Math.min(...(availableYears || [dayjs().utc().year()]))}-01-01`,
  ).utc();
  const maxDate = dayjs().utc();

  const handleFromDateChange = (date: any) => {
    const firstDate = new Date(date);
    const fromDateValue = moment(firstDate).format("YYYY-MM-DD");
    dispatch({ type: "SET_FROM_DATE", payload: { value: fromDateValue } });
    setChanged(true)
  };

  const handleToDateChange = (date: any) => {
    const toDate = new Date(date);
    const toDateValue = moment(toDate).format("YYYY-MM-DD");
    dispatch({ type: "SET_TO_DATE", payload: { value: toDateValue } });
    if (fromDate && toDateValue < fromDate) {
      setDateError(true);
    } else {
      dispatch({
        type: "SET_TO_DATE",
        payload: { value: toDateValue, period },
      });
      setChanged(true)
      setDateError(false);
    }
  };

  const handleClearDates = () => {
    dispatch({ type: "CLEAR_DATES", payload: null });
    setDateError(false);
    setClearDate(true);
    setTimeout(() => {
      setClearDate(false);
    }, 100);
    window.location.reload();
  };

  useEffect(() => {
    dispatch({
      type: "SET_FROM_DATE",
      payload: { value: period.format("YYYY-MM-DD") },
    });
    dispatch({
      type: "SET_TO_DATE",
      payload: { value: period.format("YYYY-MM-DD") },
    });
    dispatch({
      type: "SET_START_AND_END_DATES",
      payload: {
        startDate: period.format("YYYY-MM-DD"),
        endDate: period.format("YYYY-MM-DD"),
      },
    });
    // eslint-disable-next-line
  }, [period, year]);

  const {
    data: auditLogs,
    isLoading,
    mutate: auditLogsMutation,
  } = useMutation(getAuditLogsData);


  const handleSearch = () => {
    setChanged(false)

    const date1: any = new Date(startDate);
    const date2: any = new Date(endDate);

    const differenceMs = Math.abs(date2 - date1);

    const differenceMonths = differenceMs / (1000 * 60 * 60 * 24 * 30);

    if (differenceMonths > 1) {
      notify.error(
        "We’re not able to search the audit log beyond a 30 day window. Please adjust your Start and End Dates and try again. ",
      );
    } else {
      auditLogsMutation({
        fromDate: startDate,
        toDate: endDate,
      });
    }
  }

  useEffect(() => {
    handleSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const handleDetailsModalClose = () => {
    setSelectedRowData(undefined);
  };

  const AUDIT_LOGS_COLUMNS_MAIN: MRT_ColumnDef<any>[] = [
    {
      header: "Activity Type",
      accessorKey: "eventType",
      Cell: ({ cell }) => {
        const value = cell.getValue()
        if (isNullOrUndefined(value)) {
          return EMPTY_CELL_VALUE;
        }
        return (
          <Box sx={{ display: "flex", width: '70px' }}>
            <Tooltip title={value as string}>
              <Box>
                {value as any}
              </Box>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      header: "Resources Affected",
      accessorKey: "className",
      Cell: ({ cell, row }) => {
        const value = cell.getValue()

        if (isNullOrUndefined(value)) {
          return EMPTY_CELL_VALUE;
        }
        return (
          <Box sx={{ display: "flex", width: '30px' }}>
            <Tooltip title={value as string}>
              {row.original?.className === "Bill" ?
                <Link style={{ textDecoration: 'none', color: 'black' }} target="_blank" to={`/documents/summary/${row.original?.persistedObjectId}`}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <EsgFileIcon />
                    <EsgPrimaryLabel sx={{ mt: '4px' }}>{value as any}</EsgPrimaryLabel>
                  </Box>
                </Link> :
                <Box>
                  {value as any}
                </Box>}
            </Tooltip>
          </Box>
        );
      },
    },
    {
      header: "User",
      accessorKey: "userName",
      Cell: ({ cell, row }) => {
        const value = cell.getValue()
        if (isNullOrUndefined(value)) {
          return EMPTY_CELL_VALUE;
        }

        const firstName = row.original?.firstName
        const lastName = row.original?.lastName

        const uploaderName = `${firstName} ${lastName}`;

        return (
          <Box sx={{ display: "flex" }}>
            <Tooltip title={value as string}>
              <Box sx={{ width: "50px" }}>
                <Avatar
                  sx={{
                    width: "34px",
                    height: "34px",
                  }}
                >
                  {getInitials(uploaderName)}
                </Avatar>
              </Box>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      header: "TimeStamp",
      accessorKey: "createDate",
      Cell: ({ cell }) => {
        const value = cell.getValue()
        if (isNullOrUndefined(value)) {
          return EMPTY_CELL_VALUE;
        }
        return (
          <Box sx={{ display: "flex", width: '30px' }}>
            <Tooltip title={value as string}>
              <Box>
                {moment(value as string).format('YYYY/MM/DD HH:mm:ss') as any}
              </Box>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      header: "Change Log",
      accessorKey: "context",
      Cell: ({ cell }) => {
        const value = cell.getValue()
        const context = JSON.parse(value as string)

        if (isNullOrUndefined(value)) {
          return EMPTY_CELL_VALUE;
        }

        // First Two Data's for Table
        const keysToShow = Object.keys(context).slice(0, 2);

        // Tooltip Data
        const tooltipData = <ul>
          {Object.keys(context).map((key) => (
            <li key={key}>
              {key} : {context[key].oldValue} {"->"} {context[key].newValue}
            </li>
          ))}
        </ul>

        return (
          <Box sx={{ display: "flex", width: "150px" }}>
            <Tooltip title={tooltipData as any}>
              <div>
                {keysToShow.map((key) => {
                  if (key === "parentData") return null
                  return (
                    <div key={key}>
                      • {key} : {context[key].oldValue} {"->"} {context[key].newValue}
                    </div>
                  )
                })}
                &nbsp; {Object.keys(context).length > 2 && "... more"}
              </div>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const tableData = auditLogs || [];
  const datePickerData = [
    {
      label: "Start Date",
      onAcceptFn: handleFromDateChange,
      value: dayjs(startDate),
    },
    {
      label: "End Date",
      onAcceptFn: handleToDateChange,
      value: dayjs(endDate),
    }
  ]

  return (
    <MarginWrapper>
      <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
        {!isYearly && (
          <Stack
            direction="row"
            mb={2}
            spacing={2}
            sx={{ width: dateError ? "30%" : "26%" }}
          >
            {datePickerData.map((obj) =>
              <EsgDatePickerField
                label={obj.label}
                openTo="day"
                minDate={minDate}
                maxDate={maxDate}
                views={["year", "month", "day"]}
                format={getDateFormatset()}
                disableFuture
                onChange={() => { }}
                onAccept={obj.onAcceptFn}
                value={obj.value}
                clearDate={clearDate}
                emission
              />
            )}
            {dateError && (
              <Box sx={{ color: COLORS.Fuzzy_Brown, fontSize: "0.75rem" }}>
                Invalid date selection. Please choose a date after the From
                Date.
              </Box>
            )}
          </Stack>
        )}
        <EsgCommonButton variant={isChanged ? "contained" : "outlined"} sx={{ width: "120px", color: isChanged ? "white" : COLORS.Romance }} onClick={handleSearch}>Search</EsgCommonButton>
        <EsgCommonButton variant="outlined" sx={{ width: "120px", color: COLORS.Romance }} onClick={handleClearDates}>Clear Dates</EsgCommonButton>
      </Box>
      {isLoading && <Loading />}
      <div>
        {isLoading ? (
          <Loading />
        ) : (
          <MaterialReactTable
            columns={AUDIT_LOGS_COLUMNS_MAIN}
            data={tableData}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enablePagination={false}
            enableRowVirtualization
            onGlobalFilterChange={setGlobalFilter}
            state={{
              globalFilter,
            }}
            initialState={{ density: 'compact' }}
            muiTableContainerProps={{
              sx: {
                "& thead tr:first-of-type th:first-of-type": {
                  paddingLeft: "20px",
                },
                "& tbody tr td:first-of-type": {
                  paddingLeft: "20px",
                },
                "& tbody tr:first-of-type td > p": {
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                },
                height: "calc(100vh - 255px)",
                border: "1px solid #D9D9D9",
                borderRadius: "10px",
                minWidth: "100%",
                width: "100%",
                maxWidth: "100%",
                "@media (min-width: 600px)": {
                  width: "100%",
                },
                "@media (min-width: 960px)": {
                  width: "1100px",
                },
                "@media (min-width: 1280px)": {
                  width: "1100px",
                },
                "@media (min-width: 1440px)": {
                  width: "1100px",
                },
                "@media (min-width: 1680px)": {
                  width: "1300px",
                },
                "@media (min-width: 2400px)": {
                  width: "100%",
                },
              },
            }}
            muiTablePaperProps={{
              sx: {
                borderRadius: "10px",
                boxShadow: "none",
              },
            }}
            muiTopToolbarProps={{
              sx: {
                backgroundColor: 'white',
                '& .MuiIconButton-root': {
                  // Hide filter, sorting, and density icons
                  '&:not(:nth-of-type(1))': {
                    display: 'none',
                  },
                },
              },
            }}
            muiBottomToolbarProps={{
              sx: {
                display: "none",
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                lineHeight: "30px",
                "& .MuiBadge-root": {
                  display: "none",
                },
              },
            }}
            muiTableHeadRowProps={{
              sx: {
                "& > th": {
                  paddingTop: 1,
                  paddingBottom: 1,
                },
                height: "45px",
                backgroundColor: "#F8FAFC",
              },
            }}
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => {
                setSelectedRowData(row.original);
              },
              sx: {
                cursor: 'pointer',
                maxHeight: "76px",
                backgroundColor: "white",
              },
            })}
          />

        )}
        {!!selectedRowData && <AuditLogsDetailsModal isOpen={!!selectedRowData} data={selectedRowData} handleClose={handleDetailsModalClose} />}
      </div>
    </MarginWrapper>
  );
};

export default AuditLogsPage;
